import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, ArrowLeft, ArrowUpRight, BadgeCheck } from "lucide-react";
import { findCountry, COUNTRIES } from "@/data/countries";
import { Reveal } from "@/components/landing/Reveal";
import { Footer } from "@/components/landing/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PORTAL_URL = "https://www.dalimitglobal.com/jobs";

export default function CountryPage() {
  const { slug } = useParams();
  const country = findCountry(slug);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/jobs`).then((r) => setJobs(r.data)).catch(() => {});
  }, [slug]);

  if (!country) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6">
        <h1 className="font-head uppercase text-4xl">Country not found</h1>
        <Link to="/" className="text-[#ff3b30] font-head uppercase">← Back home</Link>
      </div>
    );
  }

  const countryJobs = jobs.filter((j) => j.country === country.name || j.country === "Across Europe");

  return (
    <main data-testid="country-page" className="relative">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="/" data-testid="country-logo" className="shrink-0">
            <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_daab-recruit/artifacts/kveh0gcx_file_0000000029048211809fc80c6002540d.webp" alt="DAAB Consulting Recruitment Services" className="h-9 md:h-11 w-auto object-contain" />
          </a>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="country-back" className="inline-flex items-center gap-2 font-body text-sm text-[#52525b] hover:text-[#0a0a0a]">
              <ArrowLeft size={16} /> All countries
            </Link>
          </div>
        </div>
      </header>

      <section className="pt-16 md:pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto">
          <Reveal>
            <p className="font-body text-sm tracking-[0.25em] uppercase text-[#ff3b30] mb-6 flex items-center gap-2">
              <MapPin size={15} /> Recruitment in
            </p>
            <h1 className="font-head uppercase tracking-tighter leading-[0.88] text-6xl md:text-8xl xl:text-9xl">
              {country.name}
            </h1>
            <p className="mt-8 font-body text-lg text-[#52525b] max-w-2xl leading-relaxed">{country.blurb}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {country.sectors.map((s) => (
                <span key={s} className="border border-[#e4e4e7] px-4 py-2 font-body text-sm text-[#52525b]">{s}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="font-head uppercase tracking-tight text-3xl md:text-4xl mb-8">Open vacancies</h2>
          {countryJobs.length === 0 ? (
            <div className="border border-dashed border-[#e4e4e7] p-10 text-center">
              <p className="font-body text-[#52525b]">
                No live vacancies listed for {country.name} right now — new projects open regularly.
              </p>
              <a href="/#find-job" className="inline-block mt-5 bg-[#ff3b30] hover:bg-[#ff5248] text-white font-head uppercase text-sm px-6 py-3">
                Register your interest
              </a>
            </div>
          ) : (
            <div className="border-t border-[#e4e4e7]">
              {countryJobs.map((job, i) => (
                <motion.a
                  key={job.id}
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`country-job-${job.id}`}
                  className="group grid grid-cols-12 gap-4 items-center py-7 border-b border-[#e4e4e7] hover:bg-[#f5f5f4] transition-colors"
                >
                  <div className="col-span-12 md:col-span-5">
                    <h3 className="font-head uppercase tracking-tight text-2xl md:text-3xl group-hover:text-[#ff3b30] transition-colors">{job.title}</h3>
                    <span className="inline-flex items-center gap-1.5 font-body text-xs text-[#52525b] mt-1"><BadgeCheck size={13} className="text-[#ff3b30]" />{job.eligibility}</span>
                  </div>
                  <div className="col-span-6 md:col-span-3 font-body text-sm font-600">{job.salary}</div>
                  <div className="col-span-6 md:col-span-3 font-body text-xs text-[#52525b]">Accommodation: {job.accommodation}</div>
                  <div className="col-span-12 md:col-span-1 flex md:justify-end"><ArrowUpRight size={24} className="text-[#a1a1aa] group-hover:text-[#ff3b30] transition-colors" /></div>
                </motion.a>
              ))}
            </div>
          )}

          <div className="mt-16">
            <h3 className="font-head uppercase tracking-tight text-2xl mb-6">Other countries</h3>
            <div className="flex flex-wrap gap-2">
              {COUNTRIES.filter((c) => c.slug !== slug).map((c) => (
                <Link key={c.slug} to={`/country/${c.slug}`} className="border border-[#e4e4e7] hover:border-[#ff3b30] px-4 py-2 font-head uppercase text-sm tracking-tight transition-colors">{c.name}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
